// ─────────────────────────────────────────────────────────────────────────────
// SHARED: Auth session  (Firebase Auth state, used by every feature)
// Types and the React context for the auth session (see components/AuthProvider.tsx).
// ─────────────────────────────────────────────────────────────────────────────
import { createContext } from "react";
import type { ConfirmationResult } from "firebase/auth";

export type StaffRole = "admin" | "editor" | "viewer";

export interface AuthUser {
  uid: string;
  /** E.164, e.g. +919876543210 */
  phone: string | null;
  email: string | null;
  /** The staff role claim (admin / editor / viewer); null for a customer. Set only by the backend. */
  role: StaffRole | null;
}

export type AuthStatus = "loading" | "signed-in" | "signed-out";

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  /** Check the SMS code against Firebase's pending confirmation and start the session. */
  confirmSmsCode: (confirmation: ConfirmationResult, code: string) => Promise<void>;
  /** Staff: username (or e-mail) + password. Throws unless the account holds a staff role. */
  signInStaff: (username: string, password: string, remember?: boolean) => Promise<void>;
  /** Staff: complete a fingerprint sign-in with the custom token the backend returned. */
  signInWithToken: (customToken: string, remember?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
